// AeroMax Savings Calculator — Phase 2 calculation engine.
// All values are derived from the configurable ASSUMPTIONS module. No hard-coded
// magic numbers live here — only the modelling formulas.

import { ASSUMPTIONS } from "./assumptions";

// ─────────────────────────────────────────────────────────────────────────────
// 1. Hot water energy demand (kWh per year)
// ─────────────────────────────────────────────────────────────────────────────
export function estimateAnnualKwhDemand(inputs, a = ASSUMPTIONS) {
  const showers_per_day = num(inputs.showers_per_day, 3);
  const shower_length_min = num(inputs.shower_length_min, 8);
  const shower_flow_lpm = num(inputs.shower_flow_lpm, 9);
  const other_usage = inputs.other_usage || "medium";
  const bath_use = inputs.bath_use || "occasional";

  // Showers — household-wide
  const showerLitresPerDay = showers_per_day * shower_length_min * shower_flow_lpm * a.showerHotRatio;

  // Other hot water uses (dishes + basin + laundry combined)
  const otherLitresPerDay = a.otherUsageLitresPerDay[other_usage] ?? a.otherUsageLitresPerDay.medium;

  // Bath/spa
  const bathLitresPerDay = (a.bathLitresPerWeek[bath_use] ?? a.bathLitresPerWeek.occasional) / 7;

  const dailyLitres = showerLitresPerDay + otherLitresPerDay + bathLitresPerDay;
  const annualKwh = dailyLitres * 365 * a.energyPerHotLitreKwh;

  // Safety floor — even a tiny household uses some hot water
  return Math.max(annualKwh, 500);
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Current system annual running cost
// ─────────────────────────────────────────────────────────────────────────────
export function currentSystemCost(inputs, a = ASSUMPTIONS) {
  const annualKwh = estimateAnnualKwhDemand(inputs, a);
  const system = inputs.current_system || "not_sure";

  switch (system) {
    case "continuous_gas": {
      const dailyCharge = (a.mainsGasDailyChargeCents / 100) * 365;
      const deliveredKwh = annualKwh / a.gasContinuousEfficiency;
      const usageCharge = (deliveredKwh * a.mainsGasVariableRateCentsPerKwh) / 100;
      return { annual: dailyCharge + usageCharge, dailyCharge, usageCharge, deliveredKwh, annualKwh };
    }
    case "gas_storage": {
      const dailyCharge = (a.mainsGasDailyChargeCents / 100) * 365;
      const deliveredKwh = annualKwh / a.gasStorageEfficiency;
      const usageCharge = (deliveredKwh * a.mainsGasVariableRateCentsPerKwh) / 100;
      return { annual: dailyCharge + usageCharge, dailyCharge, usageCharge, deliveredKwh, annualKwh };
    }
    case "lpg": {
      const deliveredKwh = annualKwh / a.lpgEfficiency;
      const bottlesPerYear = deliveredKwh / a.lpgBottleEnergyKwhEquivalent;
      const annual = bottlesPerYear * a.lpgBottleCost;
      return { annual, bottlesPerYear, deliveredKwh, annualKwh };
    }
    case "electric_storage": {
      const deliveredKwh = annualKwh / a.electricStorageEfficiency;
      const annual = (deliveredKwh * a.electricityRateCentsPerKwh) / 100;
      return { annual, deliveredKwh, annualKwh };
    }
    case "not_sure":
    default: {
      // Conservative average of gas-continuous + electric-storage
      const elec = (annualKwh / a.electricStorageEfficiency) * a.electricityRateCentsPerKwh / 100;
      const gas =
        (a.mainsGasDailyChargeCents / 100) * 365 +
        ((annualKwh / a.gasContinuousEfficiency) * a.mainsGasVariableRateCentsPerKwh) / 100;
      return { annual: (elec + gas) / 2, conservativeAssumption: true, annualKwh };
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. NeoPower annual running cost
// ─────────────────────────────────────────────────────────────────────────────
export function neopowerCost(inputs, a = ASSUMPTIONS) {
  const annualKwh = estimateAnnualKwhDemand(inputs, a);
  const effectiveCOP = a.neoPowerCOP * (a.conservativeRealWorldCOPAdjustment || 1);
  const inputElectricityKwh = annualKwh / effectiveCOP;
  const annual = (inputElectricityKwh * a.electricityRateCentsPerKwh) / 100;
  return { annual, inputElectricityKwh, effectiveCOP, annualKwh };
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Standard amortised monthly loan repayment
//    rate = decimal p.a. (0.01 for 1.00% p.a.); termYears in years.
// ─────────────────────────────────────────────────────────────────────────────
export function monthlyLoanRepayment(principal, rate, termYears) {
  if (!termYears || termYears <= 0 || rate === null || rate === undefined) return null;
  const months = termYears * 12;
  if (rate === 0) return principal / months;
  const r = rate / 12;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Full estimate — the single API the UI consumes
// ─────────────────────────────────────────────────────────────────────────────
export function calculateFullEstimate(inputs, a = ASSUMPTIONS) {
  const annualKwh = estimateAnnualKwhDemand(inputs, a);
  const current = currentSystemCost(inputs, a);
  const heatpump = neopowerCost(inputs, a);

  const annualSaving = Math.max(0, current.annual - heatpump.annual);
  const monthlySaving = annualSaving / 12;

  // Yearly projection with energy-price escalation (cumulative savings)
  const yearlyProjection = [];
  let cumulative = 0;
  for (let y = 1; y <= a.systemLifeYears; y++) {
    const escalator = Math.pow(1 + a.annualEnergyPriceIncrease, y - 1);
    const yearSaving = annualSaving * escalator;
    cumulative += yearSaving;
    yearlyProjection.push({ year: y, yearSaving, cumulative });
  }
  const fifteenYearSaving = cumulative;

  // Break-even — first year cumulative meets installed cost LOW (per Phase 2 spec).
  // ROI / payback uses $6,999 as the assumption; the range $6,999–$7,999 is for display only.
  const installedCost = a.installedCostLow;
  let breakEvenYear = null;
  for (const p of yearlyProjection) {
    if (p.cumulative >= installedCost) { breakEvenYear = p.year; break; }
  }
  const totalReturnAfterLife = fifteenYearSaving - installedCost;

  // CO₂ saving (kg/year)
  const co2BeforeKg = (() => {
    if (["continuous_gas", "gas_storage", "lpg"].includes(inputs.current_system)) {
      return annualKwh * a.co2GasFactor;
    }
    if (inputs.current_system === "electric_storage") {
      return (annualKwh / a.electricStorageEfficiency) * a.co2ElectricityFactor;
    }
    return annualKwh * ((a.co2GasFactor + a.co2ElectricityFactor) / 2);
  })();
  const co2AfterKg = heatpump.inputElectricityKwh * a.co2ElectricityFactor;
  const co2SavingKg = Math.max(0, co2BeforeKg - co2AfterKg);

  // Finance comparison (only meaningful if user selected a real option)
  const financeKey = inputs.finance_option || "none";
  const finance = computeFinance(financeKey, installedCost, monthlySaving, a);

  return {
    // headline
    annual_saving: annualSaving,
    monthly_saving: monthlySaving,
    fifteen_year_saving: fifteenYearSaving,
    current_annual_cost: current.annual,
    heat_pump_annual_cost: heatpump.annual,
    co2_saving_kg: co2SavingKg,

    // product / confidence
    recommended_system: "NeoPower Black Shield 270L",
    confidence: "High",
    cop_used: a.neoPowerCOP,
    effective_cop_used: heatpump.effectiveCOP,

    // installed cost & roi
    installed_cost_low: a.installedCostLow,
    installed_cost_high: a.installedCostHigh,
    installed_cost_midpoint: installedCost,
    installed_cost_used: installedCost,
    break_even_year: breakEvenYear,
    total_return_after_life: totalReturnAfterLife,
    system_life_years: a.systemLifeYears,
    annual_energy_price_increase: a.annualEnergyPriceIncrease,

    // chart data
    yearly_projection: yearlyProjection,

    // finance result
    finance,

    // demand / underlying
    annual_kwh_demand: annualKwh,
    current_breakdown: current,

    // assumptions snapshot (for the Assumptions Used panel)
    assumptions_used: a,
  };
}

function computeFinance(key, principal, monthlySaving, a) {
  const opt = a.financeOptions[key] || a.financeOptions.none;
  if (key === "none") {
    return { selected: false, key, label: opt.label };
  }

  // If we don't have a rate (Kiwibank / Other), we still show the panel but flag for confirmation
  if (opt.rate === null || opt.rate === undefined) {
    return {
      selected: true,
      key,
      label: opt.label,
      rate: null,
      termYears: opt.termYears || null,
      cap: opt.cap || null,
      note: opt.note,
      monthlyRepayment: null,
      netMonthlyDifference: null,
      requiresLenderConfirmation: true,
    };
  }

  const monthlyRepayment = monthlyLoanRepayment(principal, opt.rate, opt.termYears);
  const netMonthlyDifference = monthlySaving - monthlyRepayment;
  return {
    selected: true,
    key,
    label: opt.label,
    rate: opt.rate,
    termYears: opt.termYears,
    cap: opt.cap,
    note: opt.note,
    principal,
    monthlyRepayment,
    netMonthlyDifference,
    requiresLenderConfirmation: false,
  };
}

function num(v, fallback) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}
